import { safeGetItem, safeSetItem, safeRemoveItem } from './storage';

const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHED_MESSAGES = 50;

// --- Keys ---
const conversationsKey = (userId) => `kaiylo_chat_${userId}`;
const messagesKey = (conversationId) => `kaiylo_msgs_${conversationId}`;
const KNOWN_KEYS_KEY = 'kaiylo_chat_keys';

// Track all cache keys so clearAll can find them
function trackKey(key) {
  try {
    const raw = safeGetItem(KNOWN_KEYS_KEY);
    const keys = raw ? JSON.parse(raw) : [];
    if (!keys.includes(key)) {
      keys.push(key);
      safeSetItem(KNOWN_KEYS_KEY, JSON.stringify(keys));
    }
  } catch {
    // ignore
  }
}

// --- Conversations ---

export function getCachedConversations(userId) {
  if (!userId) return null;
  try {
    const raw = safeGetItem(conversationsKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL) {
      safeRemoveItem(conversationsKey(userId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function setCachedConversations(userId, conversations) {
  if (!userId) return;
  const key = conversationsKey(userId);
  try {
    safeSetItem(key, JSON.stringify({
      data: conversations,
      timestamp: Date.now()
    }));
    trackKey(key);
  } catch {
    // ignore — quota exceeded etc.
  }
}

export function updateCachedConversation(userId, convId, updates) {
  if (!userId || !convId) return;
  const cached = getCachedConversations(userId);
  if (!cached) return;
  const updated = cached.data.map(conv =>
    conv.id === convId ? { ...conv, ...updates } : conv
  );
  setCachedConversations(userId, updated);
}

// Get the ISO timestamp of when conversations were last cached (for sync endpoint)
export function getCacheTimestamp(userId) {
  const cached = getCachedConversations(userId);
  if (!cached?.timestamp) return null;
  return new Date(cached.timestamp).toISOString();
}

// Écrit la liste affichée sans toucher à l'heure de la dernière synchro (le
// `since` de la synchro par date) : un changement reçu en direct ne prouve pas
// que la liste soit à jour. Seulement sur un cache déjà créé par une synchro.
export function saveCachedConversations(userId, conversations) {
  const cached = getCachedConversations(userId);
  if (!cached) return;
  try {
    safeSetItem(conversationsKey(userId), JSON.stringify({ data: conversations, timestamp: cached.timestamp }));
  } catch {
    // ignore — quota exceeded etc.
  }
}

// Synchro par date réussie : `syncedAt` = début de la requête (un message
// arrivé pendant la requête sera repris à la suivante).
export function markConversationsSynced(userId, syncedAt) {
  const cached = getCachedConversations(userId);
  if (!cached) return;
  try {
    safeSetItem(conversationsKey(userId), JSON.stringify({ data: cached.data, timestamp: syncedAt }));
  } catch {
    // ignore
  }
}

// Conversations renvoyées par la synchro fusionnées dans `list` (remplacées ou
// ajoutées), la plus récente en premier.
export function mergeConversations(list, synced) {
  const map = new Map(list.map(c => [c.id, c]));
  synced.forEach(conv => map.set(conv.id, conv));
  return [...map.values()].sort((a, b) => {
    const aTime = a.last_message_at || a.created_at || '';
    const bTime = b.last_message_at || b.created_at || '';
    return new Date(bTime) - new Date(aTime);
  });
}

// --- Messages ---

export function getCachedMessages(conversationId) {
  if (!conversationId) return null;
  try {
    const raw = safeGetItem(messagesKey(conversationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL) {
      safeRemoveItem(messagesKey(conversationId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function setCachedMessages(conversationId, messages, nextCursor) {
  if (!conversationId) return;
  const key = messagesKey(conversationId);
  try {
    // Only cache the latest messages (tail)
    const toCache = messages.slice(-MAX_CACHED_MESSAGES);
    safeSetItem(key, JSON.stringify({
      messages: toCache,
      nextCursor: nextCursor || null,
      timestamp: Date.now()
    }));
    trackKey(key);
  } catch {
    // ignore
  }
}

export function appendCachedMessage(conversationId, message) {
  if (!conversationId || !message) return;
  const cached = getCachedMessages(conversationId);
  if (!cached) return;
  // Avoid duplicates
  if (cached.messages.some(m => m.id === message.id)) return;
  const updated = [...cached.messages, message].slice(-MAX_CACHED_MESSAGES);
  setCachedMessages(conversationId, updated, cached.nextCursor);
}

/** Merge updates into a cached message (e.g. after edit). */
export function patchCachedMessage(conversationId, messageId, updates) {
  if (!conversationId || !messageId || !updates || typeof updates !== 'object') return;
  const cached = getCachedMessages(conversationId);
  if (!cached?.messages?.length) return;
  const updated = cached.messages.map((m) =>
    m.id === messageId ? { ...m, ...updates } : m
  );
  setCachedMessages(conversationId, updated, cached.nextCursor);
}

// --- Cleanup ---

export function clearAllChatCache() {
  try {
    const raw = safeGetItem(KNOWN_KEYS_KEY);
    if (raw) {
      const keys = JSON.parse(raw);
      keys.forEach(key => safeRemoveItem(key));
    }
    safeRemoveItem(KNOWN_KEYS_KEY);
  } catch {
    // ignore
  }
}
